import { Camera, ThreeEvent } from "@react-three/fiber";
import { Vector3 } from "three";

export type GestureMode = 'dragging' | 'pinching' | null;

export default class PointerGesture {
	private _logEvents = false;
	private _evCache: ThreeEvent<PointerEvent>[] = [];
	private _currentGesture: GestureMode = null;
	private _onDragHandler?: (delta: any) => void;
	private _onPinchHandler?: (diff: any) => void;
	private _onPointerMoveHandler?: (mode: GestureMode) => void;
	private _onPointerUpHandler?: () => void;
	private _isActive = false;
	private _pinchDistanceStart = 0;
	private _pinchDistanceEnd = 0;
	private _pinchInitial = 0;
	private _dragInitial: Vector3 | null = null;
	private _currentDiff = 0;
	private _camera: Camera;

	constructor(camera: Camera) {
		this._camera = camera;
		this.log('Pointer Gesture instantiated');
	}

	public _cleanup = (): void => {
		this._isActive = false;
    this._pinchDistanceStart = 0;
		this._pinchDistanceEnd = 0;
		this._pinchInitial = 0;
		this._currentDiff = 0;
		this._currentGesture = null;
		this._evCache = [];
		this.log('CLEANUP COMPLETE');
	};

  // If second argument isn't provided, just treats the first as a string to log to console
	private log = (prefix: string, ev?: ThreeEvent<PointerEvent>) => {
		if (!this._logEvents) return;
		let logMsg = '';
		if (ev) {
			logMsg = `${prefix}: pointerID = ${ev.pointerId} ; pointerType = ${ev.pointerType}; isPrimary = ${ev.isPrimary}; 
			mode = ${this._currentGesture}; screen position: ( ${ev.clientX}, ${ev.clientY} ) event cache size = ${this._evCache.length}			
			`;
			// current pinch factor: ${this._currentPinchFactor} previous pinch factor ${this._previousPinchFactor}
		} else {
			logMsg = `${prefix}`;
		}
		console.log(logMsg);
	};

	private remove_event = (ev: ThreeEvent<PointerEvent>): void => {
		// this.log('Removing event: ', ev )
		for (let i = 0; i < this._evCache.length; i++) {
			if (this._evCache[i].pointerId === ev.pointerId) {
				this._evCache.splice(i, 1);
				break;
			}
		}
	};

	private _dragGesture = (ev: ThreeEvent<PointerEvent>) => {
		const xV3 = new Vector3(ev.intersections[0].point.x, ev.intersections[0].point.y, -5);
		const coords = this._camera.worldToLocal(xV3);
		if (!this._dragInitial) {
			this._dragInitial = new Vector3(coords.x, coords.y, -5);
			return; // Nothing meaningful to emit
		}

		let delta = [
			(coords.x - this._dragInitial.x),
			(coords.y - this._dragInitial.y)
		]

		this._dragInitial = new Vector3(coords.x, coords.y, -5);
		this._currentGesture = 'dragging';
		this._onDragHandler?.(delta);
	};

	private _pinchGesture = () => {
		this._currentGesture = 'pinching';
		this._onPinchHandler?.(this._normaliseDiffFactor());
	};

  // To be called when pinching, returns a normalised scaling factor 
	private _normaliseDiffFactor = (): number => {
		const dx = this._evCache[0].clientX - this._evCache[1].clientX;
		const dy = this._evCache[0].clientY - this._evCache[1].clientY;
		this._pinchDistanceEnd = Math.sqrt(dx * dx + dy * dy);
		
		this.log(`Pinch Distance Start: ${this._pinchDistanceStart}`);
		this.log(`Pinch Distance End: ${this._pinchDistanceEnd}`);
		const factor = this._pinchDistanceEnd / this._pinchDistanceStart;

		if (!this._pinchInitial) this._pinchInitial = factor;
		if (isNaN(factor) || factor === Infinity) return 1;
		this._currentDiff = this._pinchInitial * factor;
		return this._currentDiff;
	};

  public _pointerDownHandler = (ev: ThreeEvent<PointerEvent>): void => {
    // Add to event cache
		this._evCache.push(ev);
    
    // Active flag is true when the first pointer event is registered
		if (!this._isActive) {
			this._isActive = true;
			this.log('Pointer Gesture Active', ev);
    }
    
    // If we aren't pinching already and there are 2 events then we need to calculate the initial pinch distance
		if (this._evCache.length === 2 && this._currentGesture !== 'pinching') {
			const dx = this._evCache[0].clientX - this._evCache[1].clientX;
			const dy = this._evCache[0].clientY - this._evCache[1].clientY;
			this._pinchDistanceEnd = this._pinchDistanceStart = Math.sqrt(
				dx * dx + dy * dy
			);
    }
    
		this.log('Pointer Down: ', ev);
	};

	public _pointerMoveHandler = (ev: ThreeEvent<PointerEvent>): void => {
		this.log('Pointer Move: ', ev);
		
		// Find this event in the cache and update its record with this event
		for (let i = 0; i < this._evCache.length; i++) {
			if (ev.pointerId === this._evCache[i].pointerId) {
				this._evCache[i] = ev;
			}
		}
		
		// 2 touch events - pinch gesture
		if (this._evCache.length === 2 && this.currentGesture !== 'dragging') {
      this._pinchGesture();
			// 1 touch event - drag gesture
		} else if (this._evCache.length === 1 && this.currentGesture !== 'pinching') {
			// this._dragGesture(ev);	// ! Removed drag functionality
			// Neither dragging nor pinching - typically no pointer events but a failsafe for > 2
    } else {
			this._currentGesture = null;
		}
		this._onPointerMoveHandler?.(this._currentGesture);
	};
	
	public _pointerUpHandler = (ev: ThreeEvent<PointerEvent>): void => {
		this.log(`-----=====-----===== POINTER UP =====-----=====-----`)
		this._pinchInitial = this._currentDiff;
		this._dragInitial = null;
		// this.remove_event(ev);
		this._evCache = [];	// Hard reset on every pointer up
		this._currentGesture = null;
		this.log(ev.type, ev);
		this._onPointerUpHandler?.();
	};

	// Getters
	get isActive(): boolean {
		return this._isActive;
	}
	get currentGesture(): 'dragging' | 'pinching' | null {
		return this._currentGesture;
	}

	// Setters
	set onDragHandler(onDragHandler: (delta: number[]) => any) {
		this._onDragHandler = onDragHandler;
	}
	set onPinchHandler(onPinchHandler: (diff: number) => any) {
		this._onPinchHandler = onPinchHandler;
	}
	set onPointerMoveHandler(onMoveHandler: (mode: GestureMode) => void) {
		this._onPointerMoveHandler = onMoveHandler;
	}
	set onPointerUpHandler(onUpHandler: () => void) {
		this._onPointerUpHandler = onUpHandler;
	}
	set logEvents(logEvents: boolean) {
		this._logEvents = logEvents;
	}
}
